import type { Employee } from "types/employee";
import type {
  ProfileCompanyData,
  ProfileSocialLink,
} from "../employeeProfileModel";
import type { CardAppearance } from "./appearance";

export type { CardAppearance } from "./appearance";
export {
  DEFAULT_CARD_APPEARANCE,
  normalizeCardAppearance,
} from "./appearance";

/** Layout keys — must match CARD_TEMPLATE.Code in the database. */
export const CARD_TEMPLATE_CODES = {
  Classic: "classic",
  Minimal: "minimal",
  Bold: "bold",
  Executive: "executive",
  Aurora: "aurora",
  Portrait: "portrait",
  Connect: "connect",
} as const;

export type CardTemplateCode =
  (typeof CARD_TEMPLATE_CODES)[keyof typeof CARD_TEMPLATE_CODES];

export type CardTemplateId = number;

/** Default when profile has no template saved. */
export const DEFAULT_CARD_TEMPLATE_ID = 1;

/** Templates offered in the design picker (Minimal / Aurora retired). */
export const SELECTABLE_CARD_TEMPLATE_CODES: ReadonlySet<CardTemplateCode> =
  new Set([
    CARD_TEMPLATE_CODES.Classic,
    CARD_TEMPLATE_CODES.Bold,
    CARD_TEMPLATE_CODES.Executive,
    CARD_TEMPLATE_CODES.Portrait,
    CARD_TEMPLATE_CODES.Connect,
  ]);

export type SectionKey = "contact" | "company" | "summary";

export type CardTheme = {
  start: string;
  end: string;
};

export type CardTemplateProps = {
  employee: Employee;
  companyData: ProfileCompanyData;
  theme: CardTheme;
  appearance: CardAppearance;
  isPreview: boolean;
  isExpired: boolean;
  isDemoAccount: boolean;
  employeeName: string;
  avatarSrc: string;
  avatarInitials: string;
  logoSrc: string;
  socialLinks: ProfileSocialLink[];
  onAddContact: () => void;
  /** Only Classic uses accordion sections. */
  sectionsOpen?: Record<SectionKey, boolean>;
  toggle?: (key: SectionKey) => void;
};

export type CardTemplateMeta = {
  id: number;
  code: string;
  name: string;
  description: string;
  previewStyle: string;
  sortOrder: number;
};

/**
 * Offline / API-down fallback. Keep aligned with CardTemplateConfiguration seed.
 * Prefer loading live rows from GET /cardtemplate.
 */
export const FALLBACK_CARD_TEMPLATES: CardTemplateMeta[] = [
  {
    id: 1,
    code: CARD_TEMPLATE_CODES.Classic,
    name: "Classic",
    description: "Wave header with organized sections",
    previewStyle: CARD_TEMPLATE_CODES.Classic,
    sortOrder: 1,
  },
  {
    id: 2,
    code: CARD_TEMPLATE_CODES.Minimal,
    name: "Minimal",
    description: "Clean, centered, and spacious",
    previewStyle: CARD_TEMPLATE_CODES.Minimal,
    sortOrder: 2,
  },
  {
    id: 3,
    code: CARD_TEMPLATE_CODES.Bold,
    name: "Bold",
    description: "Strong brand hero with centered profile",
    previewStyle: CARD_TEMPLATE_CODES.Bold,
    sortOrder: 3,
  },
  {
    id: 4,
    code: CARD_TEMPLATE_CODES.Executive,
    name: "Executive",
    description: "Premium split layout for corporate profiles",
    previewStyle: CARD_TEMPLATE_CODES.Executive,
    sortOrder: 4,
  },
  {
    id: 5,
    code: CARD_TEMPLATE_CODES.Aurora,
    name: "Aurora",
    description: "Soft gradient atmosphere with glass panel",
    previewStyle: CARD_TEMPLATE_CODES.Aurora,
    sortOrder: 5,
  },
  {
    id: 6,
    code: CARD_TEMPLATE_CODES.Portrait,
    name: "Portrait",
    description: "Cinematic photo hero with contact panel",
    previewStyle: CARD_TEMPLATE_CODES.Portrait,
    sortOrder: 6,
  },
  {
    id: 7,
    code: CARD_TEMPLATE_CODES.Connect,
    name: "Connect",
    description: "Modern profile with share details",
    previewStyle: CARD_TEMPLATE_CODES.Connect,
    sortOrder: 7,
  },
];

/** Active catalog for the design picker (excludes retired layouts). */
export const FALLBACK_SELECTABLE_CARD_TEMPLATES: CardTemplateMeta[] =
  FALLBACK_CARD_TEMPLATES.filter((t) =>
    SELECTABLE_CARD_TEMPLATE_CODES.has(t.code as CardTemplateCode)
  );

const CODE_BY_ID: Record<number, CardTemplateCode> = Object.fromEntries(
  FALLBACK_CARD_TEMPLATES.map((t) => [t.id, t.code])
) as Record<number, CardTemplateCode>;

export function isKnownTemplateCode(value: string): value is CardTemplateCode {
  return (Object.values(CARD_TEMPLATE_CODES) as string[]).includes(value);
}

export function resolveCardTemplateCode(
  value: unknown,
  codeHint?: string | null
): CardTemplateCode {
  if (codeHint && isKnownTemplateCode(codeHint)) return codeHint;
  if (typeof value === "string" && isKnownTemplateCode(value)) return value;
  const n = typeof value === "number" ? value : Number(value);
  if (!Number.isNaN(n) && CODE_BY_ID[n]) return CODE_BY_ID[n];
  return CARD_TEMPLATE_CODES.Classic;
}

export function normalizeCardTemplateId(value: unknown): CardTemplateId {
  const n = typeof value === "number" ? value : Number(value);
  if (!Number.isNaN(n) && n > 0) return n;
  return DEFAULT_CARD_TEMPLATE_ID;
}
